// src/components/ViewPastProcessingJobs.tsx

import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  PlusCircleOutlined,
  ReloadOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  List,
  Modal,
  Pagination,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import useSWR, { mutate } from "swr";
import AdditionalDocumentGeneration from "@/components/AdditionalDocumentGeneration";
import ClearJobConfirmation from "@/components/ClearJobConfirmation";
import CustomerServiceRequest from "@/components/CustomerServiceRequest";
import DisplayJobSummaryData from "@/components/DisplayJobSummaryData";
import JobStatusMonitor from "@/components/JobStatusMonitor";
import { notify } from "@/components/Notification";
import ProcessedDocumentResultsViewer from "@/components/ProcessedDocumentResultsViewer";
import useAppStore from "@/store/useAppStore";
import type { Document, FastApiJobSummaryResponse, ProcessingJob } from "@/types";
import { getDisplayFileType, handleDownload, safeStringify } from "@/utils/api";
import { isEligibleForSupport } from "@/utils/documentUtils";

const { Title } = Typography;

const normalizeStatus = (status: string): string => {
  // Trim any legacy trailing underscores and normalize casing
  const s = status.endsWith("_") ? status.slice(0, -1) : status;
  return s;
};

const getStatusColor = (status: string) => {
  const normalizedStatus = normalizeStatus(status).toLowerCase();
  // Treat any failure-like status as red
  if (
    normalizedStatus === "processing_failed" ||
    normalizedStatus.endsWith("_failed") ||
    normalizedStatus.includes("generation_failed") ||
    normalizedStatus === "failed"
  ) {
    return "red";
  }
  switch (normalizedStatus) {
    case "completed":
      return "green";
    case "files_regenerated":
      return "orange";
    case "processing":
    case "processing_additional":
      return "blue";
    default:
      return "blue";
  }
};

const isJobActionable = (job: ProcessingJob) => {
  const nonActionableStates = ["processing", "processing_additional"];
  const normalizedStatus = normalizeStatus(job.status);
  return !nonActionableStates.includes(normalizedStatus);
};

const fetcher = (url: string) => axios.get(url).then((res) => res.data);

function useJobs(excludeCleared: boolean) {
  const { data, error } = useSWR<ProcessingJob[]>(
    ["/api/processing_jobs", excludeCleared],
    ([url, exc]) =>
      axios.get<ProcessingJob[]>(url, { params: { excludeCleared: exc } }).then((res) => res.data),
  );
  return {
    jobs: data || [],
    isLoading: !data && !error,
    isError: !!error,
    error,
  };
}

async function bulkFetchDocuments(jobIds: string[]): Promise<Record<string, Document>> {
  const promises = jobIds.map((jobId) =>
    axios
      .get<Document>(`/api/get-document-from-job?jobId=${encodeURIComponent(jobId)}`)
      .then((res) => [jobId, res.data]),
  );
  const entries = await Promise.all(promises);
  return Object.fromEntries(entries);
}

function useDocumentsMap(jobIds: string[]) {
  const sortedIds = [...jobIds].sort();
  const { data, error, isLoading } = useSWR(
    jobIds.length > 0 ? ["/api/bulk_get_documents", sortedIds] : null,
    async () => bulkFetchDocuments(sortedIds),
  );
  return {
    documentsMap: data || {},
    documentsError: error,
    documentsLoading: isLoading,
  };
}

interface MobileJobItemProps {
  job: ProcessingJob;
  document: Document | undefined;
  showJobDetails: (job: ProcessingJob) => void;
  handleDownloadZip: (job_id: string) => void;
  downloadingJobs: Record<string, boolean>;
  handleClearJob: (job_id: string) => void;
  renderSupportButton: (job: ProcessingJob) => React.ReactNode;
  setAdditionalDocGenJob: (job: ProcessingJob | null) => void;
  monitoredJobs: Set<string>;
  setMonitoredJobs: React.Dispatch<React.SetStateAction<Set<string>>>;
  fetchJobs: () => Promise<void>;
  onStatusClick: (jobId: string) => void;
}

const MobileJobItem = React.memo<MobileJobItemProps>(
  ({
    job,
    document,
    showJobDetails,
    handleDownloadZip,
    downloadingJobs,
    handleClearJob,
    renderSupportButton,
    setAdditionalDocGenJob,
    monitoredJobs,
    setMonitoredJobs,
    fetchJobs,
    onStatusClick,
  }) => {
    if (!document) {
      return (
        <List.Item>
          <Alert message="Loading document information..." type="info" showIcon />
        </List.Item>
      );
    }
    const optimization_qualityScore = document?.optimization_qualityScore;

    return (
      <List.Item className="px-0 w-full">
        <Card className="w-full rounded-lg shadow hover:shadow-md transition-shadow">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Tooltip title={document.originalFileName}>
                <span
                  className="hover:text-blue-600 transition-colors duration-300 truncate block max-w-xs cursor-pointer"
                  onClick={() => showJobDetails(job)}
                >
                  {document?.originalFileName || "Loading..."}
                </span>
              </Tooltip>

              <div className="flex items-center gap-2">
                {renderSupportButton(job)}
                {isJobActionable(job) && (
                  <Tooltip title="Remove job">
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleClearJob(job.job_id)}
                      size="small"
                      className="p-1 hover:bg-transparent transition-colors duration-200"
                    />
                  </Tooltip>
                )}
              </div>
            </div>

            <div className="flex justify-between items-center flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Tag color="blue">{getDisplayFileType(document?.originalMimeType) || "N/A"}</Tag>
                {monitoredJobs.has(job.job_id) && (
                  <JobStatusMonitor
                    jobId={job.job_id}
                    originalStatus="completed"
                    onStatusUpdate={(status) => {
                      fetchJobs();
                      if (status === "completed") {
                        setMonitoredJobs((prev) => {
                          const newSet = new Set(prev);
                          newSet.delete(job.job_id);
                          return newSet;
                        });
                      }
                    }}
                    onError={(error) => {
                      setMonitoredJobs((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(job.job_id);
                        return newSet;
                      });
                      fetchJobs();
                    }}
                  />
                )}
                <Tag
                  color={getStatusColor(job.status)}
                  className="cursor-pointer p-2 active:opacity-70 hover:opacity-90 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusClick(job.job_id);
                  }}
                >
                  {normalizeStatus(job.status).toUpperCase()}
                </Tag>
              </div>
            </div>

            <div className="text-sm text-gray-500">{new Date(job.created_at).toLocaleString()}</div>

            <div className="text-sm text-gray-700 space-y-1">
              <div>Progress: {job.progress_percentage}%</div>
              {job.status === "completed" &&
              optimization_qualityScore !== null &&
              optimization_qualityScore !== undefined ? (
                <Tooltip title="Document optimization quality score">
                  <span
                    className={`
                    ${optimization_qualityScore >= 80 ? "text-green-600" : optimization_qualityScore >= 60 ? "text-yellow-600" : "text-red-600"} 
                    font-medium
                  `}
                  >
                    Quality Score: {optimization_qualityScore.toFixed(1)}
                  </span>
                </Tooltip>
              ) : (
                <div>Quality Score: N/A</div>
              )}
            </div>

            <div className="flex flex-col gap-2 mt-2">
              <Button
                icon={<EyeOutlined />}
                onClick={() => showJobDetails(job)}
                size="middle"
                block
                className="text-left hover:bg-gray-100 transition-colors duration-200"
              >
                View Details
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => handleDownloadZip(job.job_id)}
                loading={downloadingJobs[job.job_id]}
                size="middle"
                block
                className="text-left hover:bg-gray-100 transition-colors duration-200"
              >
                Download Files
              </Button>
              <Button
                icon={<PlusCircleOutlined />}
                onClick={() => setAdditionalDocGenJob(job)}
                size="middle"
                block
                disabled={!isJobActionable(job) || monitoredJobs.has(job.job_id)}
                className="bg-green-500 hover:bg-green-600 text-white disabled:bg-gray-300 text-left transition-colors duration-200"
              >
                Generate More
              </Button>
            </div>
          </div>
        </Card>
      </List.Item>
    );
  },
);
MobileJobItem.displayName = "MobileJobItem";

const ViewPastProcessingJobs: React.FC = () => {
  const screens = useBreakpoint();
  const { userInfo, error, setError } = useAppStore();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const isMobile = !screens.md;

  const excludeCleared = true;
  const { jobs: fetchedJobs, isLoading: loadingJobs, error: jobsError } = useJobs(excludeCleared);

  const [selectedJob, setSelectedJob] = useState<ProcessingJob | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [downloadingJobs, setDownloadingJobs] = useState<Record<string, boolean>>({});
  const [additionalDocGenJob, setAdditionalDocGenJob] = useState<ProcessingJob | null>(null);
  const [monitoredJobs, setMonitoredJobs] = useState<Set<string>>(new Set());
  const [summaryModalVisible, setSummaryModalVisible] = useState(false);
  const [summaryJobId, setSummaryJobId] = useState<string>("");
  const [optimisticallyClearedJobs, setOptimisticallyClearedJobs] = useState<Set<string>>(
    new Set(),
  );

  const [clearJobConfirmation, setClearJobConfirmation] = useState<{
    isOpen: boolean;
    jobId: string | null;
    timeoutId: number | null;
  }>({
    isOpen: false,
    jobId: null,
    timeoutId: null,
  });

  useEffect(() => {
    if (jobsError) {
      console.error("Error fetching processing jobs:", safeStringify(jobsError));
      setError("Failed to fetch jobs");
    }
  }, [jobsError, setError]);

  const jobs = useMemo(() => {
    return fetchedJobs.filter((job) => !optimisticallyClearedJobs.has(job.job_id));
  }, [fetchedJobs, optimisticallyClearedJobs]);

  const fetchJobs = useCallback(async () => {
    await mutate(["/api/processing_jobs", excludeCleared]);
  }, [excludeCleared]);

  const handleClearJob = useCallback(
    async (jobId: string, confirmed = false) => {
      if (!confirmed) {
        setClearJobConfirmation((prev) => ({ ...prev, isOpen: true, jobId }));
        return;
      }

      const jobToRemove = jobs.find((job) => job.job_id === jobId);
      if (!jobToRemove) return;

      setClearJobConfirmation((prev) => ({ ...prev, isOpen: false }));
      setOptimisticallyClearedJobs((prev) => {
        const newSet = new Set(prev);
        newSet.add(jobId);
        return newSet;
      });

      const timeoutId = window.setTimeout(() => {
        setOptimisticallyClearedJobs((prev) => {
          const newSet = new Set(prev);
          newSet.delete(jobId);
          return newSet;
        });
      }, 60000);

      setClearJobConfirmation((prev) => ({ ...prev, timeoutId }));

      try {
        await axios.post("/api/clear-job", { jobId });
        notify.success({ content: "Job cleared successfully" });
        if (timeoutId) {
          clearTimeout(timeoutId);
          setClearJobConfirmation((prev) => ({ ...prev, timeoutId: null }));
        }
        await fetchJobs();
      } catch (err: any) {
        console.error("Error clearing job:", safeStringify(err));
        if (timeoutId) {
          clearTimeout(timeoutId);
          setClearJobConfirmation((prev) => ({ ...prev, timeoutId: null }));
        }
        setOptimisticallyClearedJobs((prev) => {
          const newSet = new Set(prev);
          newSet.delete(jobId);
          return newSet;
        });
      }
    },
    [jobs, fetchJobs],
  );

  const performFullRefresh = useCallback(
    async (showMessages = true) => {
      try {
        const jobsResponse = await axios.get<ProcessingJob[]>("/api/processing_jobs");
        const currentJobs = jobsResponse.data;

        const isLikelyOnServer = (status: string) => {
          const s = normalizeStatus(status).toLowerCase();
          return (
            s.includes("processing") ||
            s.includes("generating") ||
            s === "starting" ||
            s === "queued" ||
            s === "in_progress"
          );
        };

        const statusUpdatePromises = currentJobs
          .filter((job) => isLikelyOnServer(job.status))
          .map(async (job) => {
            try {
              const response = await axios.get<FastApiJobSummaryResponse>(
                `/api/document_job_summary/${job.job_id}`,
              );
              if (
                response.data.changed &&
                normalizeStatus(response.data.status) !== normalizeStatus(job.status)
              ) {
                await axios.post("/api/revert-job-status", {
                  jobId: job.job_id,
                  status: response.data.status,
                  progress_percentage: response.data.progress_percentage,
                  error_message: response.data.error_message,
                  optimization_quality_score: response.data.optimization_quality_score,
                  total_input_tokens: response.data.total_input_tokens,
                  total_output_tokens: response.data.total_output_tokens,
                  last_processed: response.data.last_processed,
                  last_optimized: response.data.last_optimized,
                  generation_flags: response.data.generation_flags,
                });
              }
              return response.data;
            } catch (error) {
              if (axios.isAxiosError(error) && error.response?.status === 404) {
                // Job not found on server; treat as cleared/missing and avoid noisy errors
                if (process.env.NODE_ENV !== "production") {
                  console.warn(`Job not found on server, skipping: ${job.job_id}`);
                }
                return null;
              }
              console.error(`Error updating status for job ${job.job_id}:`, safeStringify(error));
              return null;
            }
          });

        await Promise.all(statusUpdatePromises);

        const fileUpdatePromises = currentJobs.map(async (job) => {
          try {
            await mutate(
              `/api/get_processed_files/${encodeURIComponent(job.job_id)}?checkLatest=true`,
            );
          } catch (error: any) {
            if (axios.isAxiosError(error) && error.response?.status === 304) {
              console.log(`No changes for files of job ${job.job_id}`);
            } else {
              console.error(`Error fetching files for job ${job.job_id}:`, safeStringify(error));
            }
          }
        });

        await Promise.all(fileUpdatePromises);
        await fetchJobs();
      } catch (error) {
        console.error("Error in full refresh:", safeStringify(error));
      }
    },
    [fetchJobs],
  );

  useEffect(() => {
    let intervalId: number | null = null;

    if (autoRefresh) {
      // Initial refresh when auto-refresh is turned on
      performFullRefresh(false);

      // Set up interval for subsequent refreshes
      intervalId = window.setInterval(() => {
        performFullRefresh(false);
      }, 30000); // Every 30 seconds
    }

    return () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
    };
  }, [autoRefresh, performFullRefresh]);

  useEffect(() => {
    return () => {
      if (clearJobConfirmation.timeoutId) {
        clearTimeout(clearJobConfirmation.timeoutId);
      }
    };
  }, [clearJobConfirmation.timeoutId]);

  const jobIds = useMemo(() => jobs.map((j) => j.job_id), [jobs]);
  const { documentsMap, documentsLoading, documentsError } = useDocumentsMap(jobIds);

  useEffect(() => {
    if (documentsError) {
      console.error("Error fetching documents:", safeStringify(documentsError));
    }
  }, [documentsError]);

  const renderSupportButton = useCallback((job: ProcessingJob) => {
    // No editing or file handling here; just support if eligible
    const jobProcessedFiles: [] = [];
    const eligible = isEligibleForSupport(job, jobProcessedFiles);
    const buttonProps = { size: "large" as const, block: true };

    if (eligible) {
      return (
        <CustomerServiceRequest
          jobId={job.job_id}
          job={job}
          processedFiles={jobProcessedFiles}
          {...buttonProps}
        />
      );
    } else {
      return null;
    }
  }, []);

  const handleDownloadZip = useCallback(
    async (job_id: string) => {
      if (downloadingJobs[job_id]) return;
      try {
        setDownloadingJobs((prev) => ({ ...prev, [job_id]: true }));
        await handleDownload(job_id);
        notify.success({ content: "Download started successfully" });
      } catch (error: any) {
        console.error("Error downloading zip file:", safeStringify(error));
        notify.error({ content: "Failed to download zip file" });
      } finally {
        setDownloadingJobs((prev) => ({ ...prev, [job_id]: false }));
      }
    },
    [downloadingJobs],
  );

  const showJobDetails = useCallback((job: ProcessingJob) => {
    // Just show the modal and set selectedJob; rely on ProcessedDocumentResultsViewer for details.
    setSelectedJob(job);
    setModalVisible(true);
  }, []);

  const handleStatusClick = useCallback((jobId: string) => {
    setSummaryJobId(jobId);
    setSummaryModalVisible(true);
  }, []);

  const paginatedJobs = useMemo(
    () => jobs.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [jobs, currentPage, pageSize],
  );

  const columns = useMemo(
    () => [
      {
        title: "Document Name",
        key: "originalFileName",
        render: (text: string, record: ProcessingJob) => {
          const doc = documentsMap[record.job_id];
          return (
            <Tooltip title={doc?.originalFileName || "Loading..."}>
              <span
                className="hover:text-blue-600 transition-colors duration-300 truncate block max-w-xs cursor-pointer"
                onClick={() => showJobDetails(record)}
              >
                {doc?.originalFileName || "Loading..."}
              </span>
            </Tooltip>
          );
        },
      },
      {
        title: "File Type",
        key: "originalMimeType",
        render: (text: string, record: ProcessingJob) => {
          const doc = documentsMap[record.job_id];
          return (
            <Tag color="blue">{getDisplayFileType(doc?.originalMimeType) || "Loading..."}</Tag>
          );
        },
      },
      {
        title: "Created At",
        dataIndex: "created_at",
        key: "created_at",
        render: (date: string) => new Date(date).toLocaleString(),
      },
      {
        title: "Status",
        key: "status",
        dataIndex: "status",
        render: (status: string, record: ProcessingJob) => {
          const color = getStatusColor(status);
          return (
            <Space>
              {monitoredJobs.has(record.job_id) && (
                <JobStatusMonitor
                  jobId={record.job_id}
                  originalStatus={normalizeStatus(status)}
                  onStatusUpdate={(newStatus) => {
                    fetchJobs();
                    if (normalizeStatus(newStatus) === "completed") {
                      setMonitoredJobs((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(record.job_id);
                        return newSet;
                      });
                    }
                  }}
                  onError={(error) => {
                    console.error(error.message);
                    setMonitoredJobs((prev) => {
                      const newSet = new Set(prev);
                      newSet.delete(record.job_id);
                      return newSet;
                    });
                    fetchJobs();
                  }}
                />
              )}
              <Tooltip title="Click to see detailed Status Report">
                <Tag
                  color={color}
                  className="cursor-pointer p-2 active:opacity-70 hover:opacity-90 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusClick(record.job_id);
                  }}
                >
                  {normalizeStatus(status).toUpperCase().replace("_", " ")}
                </Tag>
              </Tooltip>
              {(status === "processing_failed" ||
                status.endsWith("_failed") ||
                status.includes("generation_failed") ||
                status === "failed") && (
                <Tooltip title="Force refresh status from server">
                  <Button
                    icon={<SyncOutlined />}
                    size="small"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const response = await axios.get(`/api/job_status/${record.job_id}`);
                        if (
                          response.data.changed &&
                          normalizeStatus(response.data.status) !== normalizeStatus(status)
                        ) {
                          await fetchJobs();
                        }
                      } catch (error: any) {
                        console.error("Error refreshing status:", safeStringify(error));
                      }
                    }}
                  />
                </Tooltip>
              )}
            </Space>
          );
        },
      },
      {
        title: "Progress",
        key: "progress",
        render: (text: string, record: ProcessingJob) => `${record.progress_percentage}%`,
      },
      {
        title: "Quality Score",
        key: "optimization_quality_score",
        render: (text: string, record: ProcessingJob) => {
          const doc = documentsMap[record.job_id];
          const optimization_qualityScore = doc?.optimization_qualityScore;
          if (
            record.status === "completed" &&
            optimization_qualityScore !== null &&
            optimization_qualityScore !== undefined
          ) {
            return (
              <Tooltip title="Document optimization quality score">
                <span
                  className={`
                    ${optimization_qualityScore >= 80 ? "text-green-600" : optimization_qualityScore >= 60 ? "text-yellow-600" : "text-red-600"} font-medium
                  `}
                >
                  {optimization_qualityScore.toFixed(1)}
                </span>
              </Tooltip>
            );
          }
          return "N/A";
        },
      },
      {
        title: "Actions",
        key: "actions",
        render: (text: string, record: ProcessingJob) => (
          <Space size="middle">
            <Tooltip title="View job details and edit markdown files">
              <Button
                icon={<EyeOutlined />}
                onClick={() => showJobDetails(record)}
                className="hover:bg-gray-100 transition-all duration-300"
              >
                Details
              </Button>
            </Tooltip>
            <Tooltip title="Download results">
              <Button
                icon={<DownloadOutlined />}
                onClick={() => handleDownloadZip(record.job_id)}
                loading={downloadingJobs[record.job_id]}
                className="hover:bg-gray-100 transition-all duration-300"
              >
                Download
              </Button>
            </Tooltip>
            <Tooltip title="Generate additional derived document types for this job">
              <Button
                type="primary"
                icon={<PlusCircleOutlined />}
                onClick={() => setAdditionalDocGenJob(record)}
                disabled={!isJobActionable(record) || monitoredJobs.has(record.job_id)}
                className="bg-green-500 hover:bg-green-600 transition-all duration-300"
              >
                Generate More
              </Button>
            </Tooltip>
            {renderSupportButton(record)}
            {isJobActionable(record) && (
              <Tooltip title="Remove job">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleClearJob(record.job_id)}
                  className="hover:bg-transparent transition-colors duration-200"
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [
      documentsMap,
      monitoredJobs,
      fetchJobs,
      handleStatusClick,
      handleDownloadZip,
      downloadingJobs,
      renderSupportButton,
      showJobDetails,
      handleClearJob,
    ],
  );

  const handleManualRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    await performFullRefresh(true);
    setIsRefreshing(false);
  }, [isRefreshing, performFullRefresh]);

  return (
    <Card className="shadow-lg rounded-lg">
      <div className="container mx-auto px-0 sm:px-4 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 px-4 sm:px-0">
          <Title level={2} className="text-gray-800 mb-4 sm:mb-0 text-xl sm:text-2xl leading-tight">
            Past Document Processing Jobs
          </Title>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
            <Space className="flex flex-col sm:flex-row w-full sm:w-auto">
              <Switch
                checkedChildren="Auto Refresh On"
                unCheckedChildren="Auto Refresh Off"
                checked={autoRefresh}
                onChange={setAutoRefresh}
                className="mb-2 sm:mb-0"
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={handleManualRefresh}
                loading={loadingJobs || documentsLoading}
                className="w-full sm:w-auto hover:bg-gray-100 transition-colors duration-200"
              >
                Refresh
              </Button>
            </Space>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {(loadingJobs || documentsLoading) && jobs.length === 0 ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex justify-center items-center h-64"
            >
              <Spin size="large" />
            </motion.div>
          ) : jobs.length === 0 ? (
            <motion.div
              key="no-jobs"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Alert
                message="No Processing Jobs Found"
                description="You haven't processed any documents yet. Start by submitting a new job from the dashboard."
                type="info"
                showIcon
              />
            </motion.div>
          ) : (
            <motion.div
              key="job-table"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {isMobile ? (
                <div className="w-full px-4 sm:px-0">
                  <List
                    dataSource={paginatedJobs}
                    renderItem={(job) => (
                      <AnimatePresence key={job.job_id}>
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="w-full mb-4"
                        >
                          <MobileJobItem
                            job={job}
                            document={documentsMap[job.job_id]}
                            showJobDetails={showJobDetails}
                            handleDownloadZip={handleDownloadZip}
                            downloadingJobs={downloadingJobs}
                            handleClearJob={handleClearJob}
                            renderSupportButton={renderSupportButton}
                            setAdditionalDocGenJob={setAdditionalDocGenJob}
                            monitoredJobs={monitoredJobs}
                            setMonitoredJobs={setMonitoredJobs}
                            fetchJobs={fetchJobs}
                            onStatusClick={handleStatusClick}
                          />
                        </motion.div>
                      </AnimatePresence>
                    )}
                    pagination={{
                      current: currentPage,
                      pageSize: pageSize,
                      total: jobs.length,
                      onChange: (page, pageSize) => {
                        setCurrentPage(page);
                        setPageSize(pageSize || 10);
                      },
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total) => `Total ${total} items`,
                      className: "mt-4",
                    }}
                    className="bg-transparent"
                  />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table
                    columns={columns}
                    dataSource={paginatedJobs}
                    rowKey="job_id"
                    className="transition-colors duration-300"
                    rowClassName="hover:bg-gray-50 cursor-pointer"
                    pagination={false}
                    scroll={{ x: true }}
                  />
                </div>
              )}
              {!isMobile && (
                <Pagination
                  className="mt-4 text-right"
                  current={currentPage}
                  pageSize={pageSize}
                  total={jobs.length}
                  onChange={(page, pageSize) => {
                    setCurrentPage(page);
                    setPageSize(pageSize);
                  }}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total) => `Total ${total} items`}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {additionalDocGenJob && (
          <AdditionalDocumentGeneration
            job={additionalDocGenJob}
            onClose={() => setAdditionalDocGenJob(null)}
            onGenerated={async () => {
              setMonitoredJobs((prev) => new Set(prev).add(additionalDocGenJob.job_id));
              try {
                await mutate(
                  `/api/get_processed_files/${encodeURIComponent(additionalDocGenJob.job_id)}?checkLatest=true`,
                );
              } catch (error: any) {
                if (axios.isAxiosError(error) && error.response?.status === 304) {
                  console.log(
                    `No changes for files of job ${additionalDocGenJob.job_id} after additional generation.`,
                  );
                } else {
                  console.error(
                    `Error fetching files for job ${additionalDocGenJob.job_id}:`,
                    safeStringify(error),
                  );
                }
              }
              setAdditionalDocGenJob(null);
            }}
            userInfo={userInfo}
            documentInfo={
              documentsMap[additionalDocGenJob.job_id]
                ? {
                    originalFileName: documentsMap[additionalDocGenJob.job_id].originalFileName,
                    originalFileSize: documentsMap[additionalDocGenJob.job_id].originalFileSize,
                    totalPages: documentsMap[additionalDocGenJob.job_id].total_pages ?? 0,
                  }
                : null
            }
          />
        )}

        <Modal
          title="Job Details"
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
          }}
          footer={null}
          width="93%"
          style={{
            maxWidth: "2000px",
            top: 25,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          }}
          styles={{
            body: {
              maxHeight: "93vh",
              overflowX: "hidden",
              overflowY: "auto",
              scrollBehavior: "smooth",
            },
          }}
          wrapClassName="custom-modal-wrap"
        >
          <AnimatePresence mode="wait">
            {!selectedJob ? (
              <motion.div
                key="no-selection"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex justify-center items-center h-64"
              >
                <Spin size="large" />
              </motion.div>
            ) : (
              <motion.div
                key="job-details"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {/* ProcessedDocumentResultsViewer handles all editing, file fetching, etc. */}
                <ProcessedDocumentResultsViewer
                  job_id={selectedJob.job_id}
                  onUpdateFiles={() => {
                    // No local handling needed
                  }}
                  siderWidth={isMobile ? 0 : 450}
                  onGenerateMore={() => setAdditionalDocGenJob(selectedJob)}
                  onClose={() => {
                    setModalVisible(false);
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </Modal>

        <DisplayJobSummaryData
          jobId={summaryJobId}
          visible={summaryModalVisible}
          onClose={() => setSummaryModalVisible(false)}
        />

        {error && <Alert message={error} type="error" className="mt-4" />}
      </div>
      <ClearJobConfirmation
        isOpen={clearJobConfirmation.isOpen}
        onConfirm={() => {
          if (clearJobConfirmation.jobId) {
            handleClearJob(clearJobConfirmation.jobId, true);
          }
        }}
        onCancel={() =>
          setClearJobConfirmation((prev) => ({
            ...prev,
            isOpen: false,
            jobId: null,
          }))
        }
        fileName={
          clearJobConfirmation.jobId
            ? (() => {
                const j = jobs.find((job) => job.job_id === clearJobConfirmation.jobId);
                if (!j) return "";
                const doc = documentsMap[j.job_id];
                return doc?.originalFileName || "";
              })()
            : ""
        }
        loading={false}
      />
    </Card>
  );
};

export default ViewPastProcessingJobs;
